import { FC } from 'react'
import { GetServerSideProps } from 'next'
import getConfig from 'next/config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { UserAgent, useUserAgent } from 'next-useragent'
import { match, pathToRegexp } from 'path-to-regexp'

import { FlightAPI } from 'services/rest'
import { DefaultCurrencyForMarket, domainURL, domainLanguages, SupportedLanguagesArray, type DomainURL, normalizeHost } from 'utils/domainURL'
import { getCurrencyFromCountryCode } from 'utils/countryToCurrency'
import { GetDefaultData, GetPageData, extractFooterFromHomepageData } from 'utils/pages'
import { PAGE_MAP, RenderPage } from 'utils/pageRegistry'
import { GetCarRentalCountries } from 'services/carRentalCountries'
import { GetVisaFreeCountries } from 'services/queries'
import { fetchExperiments } from 'utils/experiments'
import {
  applyDirectFlightLabelExperiment,
  DIRECT_FLIGHT_LABEL_EXPERIMENT_KEY,
  DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY,
  resolveDirectFlightLabelVariant,
} from 'utils/directFlightLabelExperiment'
import { attachHomepageUiVariantsToRequest, setHomepageUiVariantCookies } from 'utils/uiVariants'

// API Response: { success: true, data: [{ dph: "dph_key", value: "A" | "B" }] }
const SEARCH_FORM_EXPERIMENT_KEY = 'dph_82ae4f01b2dfac61'
const EXPERIMENT_KEYS = [SEARCH_FORM_EXPERIMENT_KEY, DIRECT_FLIGHT_LABEL_EXPERIMENT_KEY]
import { fetchSmartBanners } from 'services/smartBanner'
import { getCookie } from 'utils/cookie'

import nextI18nextConfig from '../../next-i18next.config'
import { extractExpansionFromForwardedHost, getExpansionAndHost, resolveLanguageOnlyPathLocale, resolveMarketLanguageFromExpansion } from 'utils/expansionUrl'
import { isJsonString } from 'utils/isValidJson'
import { parseJson } from 'utils/json'

const { publicRuntimeConfig } = getConfig()
const processEnv = publicRuntimeConfig?.processEnv || {}

/** Sadece development veya DEBUG_SSR=1 ile etkin. Hassas veri (cookie, header) asla loglanmaz. */
const isSsrDebug = process.env.NODE_ENV === 'development' || process.env.DEBUG_SSR === '1'

interface IDebugInfo {
  countryCode?: string | null
  ipBasedCurrency?: string | null
  market?: string | string[] | null
  parsedDisplaySettings?: any
  currencyFromDisplaySettings?: any
  defaultCurrency?: string | null
  finalCurrency?: any
}

interface IHomepage {
  ua: UserAgent
  page: keyof typeof PAGE_MAP
}

const HomePage: FC<IHomepage> = ({ page, ...props }) => {
  const Comp = RenderPage(page) as any
  return Comp ? <Comp {...props} /> : null
}

export const getServerSideProps: GetServerSideProps = async ({ req, resolvedUrl, locale: i18nLocale, res, query, params }) => {
  const forwardedHost = req.headers['x-forwarded-host'] as string
  const expansionFromPathHeader = typeof req.headers['expansion-from-path'] === 'string' && req.headers['expansion-from-path'] ? req.headers['expansion-from-path'] : null
  const expansionFromForwardedHost = extractExpansionFromForwardedHost(forwardedHost)
  let expansionFromPathValue = expansionFromPathHeader || expansionFromForwardedHost || null

  // Hem query parametrelerinden hem de middleware header'larından market ve language al
  let market = (query.market as string) || (req.headers['market'] as string) || 'tr'
  let language = (query.language as string) || (req.headers['language'] as string) || 'tr'
  const notFound = req.headers['not-found'] === 'true'

  if (notFound) {
    return { notFound: true }
  }

  const normalizedHost = normalizeHost(forwardedHost) || forwardedHost
  const forwarded = Object.values(domainURL as DomainURL).find((e) => e?.includes(normalizedHost)) ? normalizedHost : null
  const hostname = forwarded ? `https://${forwarded}` : domainURL[market as string] || 'https://www.enuygun.com'
  const domain = Object.keys(domainURL as DomainURL).find((e) => domainURL[e].includes(hostname)) || market

  // Proxy pathname'i /'a rewrite ettiğinde locale yalnızca x-forwarded-host'ta kalabilir (örn. www.wingie.com/ar)
  if (expansionFromForwardedHost && !expansionFromPathHeader) {
    const resolved = resolveMarketLanguageFromExpansion(expansionFromForwardedHost, domain as string)
    market = resolved.market
    language = resolved.language
  }

  // sa.wingie.com/en gibi: proxy path'i strip edip language=en bırakırsa path_locale'i language'dan kur
  const domainDefaultLanguage = domainLanguages[domain as keyof typeof domainLanguages]
  expansionFromPathValue = resolveLanguageOnlyPathLocale(expansionFromPathValue, language as string, domainDefaultLanguage)

  const expansionLocale = typeof req.headers['expansion-locale'] === 'string' ? req.headers['expansion-locale'] : expansionFromPathValue ? `${market}-${language}` : null

  console.log('[DEBUG] SSR params:', {
    market,
    language,
    expansionLocale,
    expansionFromPath: expansionFromPathValue,
    resolvedUrl,
    forwardedHost,
  })

  if (isSsrDebug) {
  }

  console.log('[DEBUG] hostname calculated:', hostname, { normalizedHost, forwarded, market })

  // resolvedUrl "/" ile başlamıyorsa ekle, "//" veya geçersiz path durumunda fallback kullan
  const rawUrl = resolvedUrl?.startsWith('/') ? resolvedUrl : '/'
  const sanitizedResolvedUrl = rawUrl === '//' || !rawUrl ? '/' : rawUrl
  let originalUrlPathname = '/'
  try {
    originalUrlPathname = new URL(sanitizedResolvedUrl, hostname)?.pathname || sanitizedResolvedUrl
  } catch (e) {
    console.error('[SSR] URL parse error:', e)
    originalUrlPathname = '/'
  }

  const resolvedPage = Object.values(PAGE_MAP).find((item) => pathToRegexp(item).exec(originalUrlPathname)) as string

  // Eğer sayfa bulunamadıysa (örn: /us-ja/, /ja/ gibi), ana sayfayı kullan
  const finalResolvedPage = resolvedPage || PAGE_MAP.Main

  if (!finalResolvedPage) {
    return {
      notFound: true,
    }
  }

  const toMatch: any = match(finalResolvedPage, {
    decode: decodeURIComponent,
  })

  let $params = await toMatch(resolvedUrl)?.params

  if (isSsrDebug) {
    console.log('[SSR] page', { pathname: originalUrlPathname, resolvedPage: finalResolvedPage, paramsKeys: $params ? Object.keys($params) : [] })
  }
  ///API BURADAN DEĞİŞECEK
  //TODO
  // const API_ENDPOINT = market === 'tr' ? hostname + '/ucak-bileti' : expansionLocale ? `https://8656-en.enuygun.dev/${expansionLocale}` : `https://8656-${market}.enuygun.dev` FLY API ENDPOINT

  const { expansion: path_locale, host: $host } = getExpansionAndHost(
    expansionFromPathValue,
    market as string,
    hostname || domainURL[domain as string] || domainURL[market as string]
  )

  const API_ENDPOINT = path_locale ? hostname + '/' + path_locale : hostname
  if (isSsrDebug) {
    console.log('[SSR] API', { endpoint: API_ENDPOINT })
  }

  // Smart Banner: expansion path'lerde (/en/, /ae-en/) resolvedPage undefined kalır,
  // homepage fallback finalResolvedPage ile gelir — isHomepage onu kullanmalı.
  const isHomepage = finalResolvedPage === PAGE_MAP.Main
  const isMobileUA = req.headers['x-ua-device'] === 'mobile' || (req.headers['user-agent'] && /mobile/i.test(req.headers['user-agent'] as string))

  // TÜM API çağrılarını paralel çalıştır (SSR optimizasyonu)
  // Promise.allSettled kullanarak bir API hatası diğerlerini etkilemez
  const results = await Promise.allSettled([
    GetDefaultData({
      language: language,
      market: market,
      domain: domain || market,
      speedExpansion: path_locale,
      host: API_ENDPOINT,
    }),
    GetPageData(finalResolvedPage, {
      ...$params,
      language: language,
      market: market,
      domain: domain || market,
      host: API_ENDPOINT,
    }),
    GetVisaFreeCountries(market as string),
    GetCarRentalCountries(market as string, language as string),
  ])

  const defaultData = results[0].status === 'fulfilled' ? results[0].value : { endesignLocales: null, currencyConversions: null }
  const pageData = results[1].status === 'fulfilled' ? results[1].value : null
  const visaFreeCountries = results[2].status === 'fulfilled' ? results[2].value : {}
  const residenceCountries = results[3].status === 'fulfilled' ? results[3].value : []

  const { endesignLocales, currencyConversions } = defaultData
  const $response = Array.isArray(pageData) ? pageData.filter(Boolean) : null

  const homepage = $response && $response.length >= 3 && $response[3]

  // Footer verisini homepage response'undan çıkar (senkron işlem - API call yok)
  const footer = await extractFooterFromHomepageData(homepage, {
    market: market as string,
    language: language as string,
    speedExpansion: path_locale,
  })

  const $currenyRates = currencyConversions?.rates
  const $defaultCurrency = DefaultCurrencyForMarket[market as string]

  // SSR Cookie parsing - read from displaySettings cookie instead of currency cookie
  const cookieHeaderRaw = req?.headers['x-cookie'] || req?.headers['cookie'] || ''
  const cookieHeader = Array.isArray(cookieHeaderRaw) ? cookieHeaderRaw.join('; ') : cookieHeaderRaw

  const $displaySettingsFromCookies = cookieHeader
    ?.split(';')
    .find((e) => e.trim().startsWith('displaySettings='))
    ?.split('=')
    .slice(1)
    .join('=')

  const $decodedDisplaySettings = $displaySettingsFromCookies ? decodeURIComponent($displaySettingsFromCookies) : null

  const $parsedDisplaySettings = $decodedDisplaySettings && isJsonString($decodedDisplaySettings) ? parseJson($decodedDisplaySettings) : null
  const $currencyFromDisplaySettings = $parsedDisplaySettings?.currency

  // IP-based currency ayarlama (DISABLED - Kullanıcı talebi ile kapatıldı)
  // IP tabanlı currency özelliği devre dışı bırakıldı
  // Artık sadece displaySettings cookie'sindeki currency veya market default currency kullanılacak

  // let $ipBasedCurrency: string | null = null
  // const countryCode = req.headers['cf-ipcountry'] || req.headers['x-country-code'] || req.headers['country_code']

  // if (countryCode && typeof countryCode === 'string') {
  //   $ipBasedCurrency = getCurrencyFromCountryCode(countryCode, market as string, DefaultCurrencyForMarket)
  //   console.log('[IP_BASED_CURRENCY] Country code detected:', {
  //     countryCode,
  //     ipBasedCurrency: $ipBasedCurrency,
  //     market,
  //   })
  // }

  // Debug için countryCode'u muhafaza et
  const countryCode = req.headers['cf-ipcountry'] || req.headers['x-country-code'] || req.headers['country_code']
  const $ipBasedCurrency: string | null = null // IP-based currency disabled

  // Currency belirleme: DisplaySettings > Default market currency (IP-based kaldırıldı)
  const $finalCurrencyIso = $currencyFromDisplaySettings?.iso || $defaultCurrency
  const $finalCurrencyConversion = $currencyFromDisplaySettings?.conversion || ($currenyRates ? $currenyRates[$finalCurrencyIso] : 1)

  if (isSsrDebug) {
    console.log('[SSR] currency', {
      source: $currencyFromDisplaySettings?.iso ? 'displaySettings' : 'marketDefault',
      iso: $finalCurrencyIso,
      countryCode: countryCode || null,
    })
  }

  // debugInfo yalnızca server log için; pageProps'a konmaz (cookie/header sızıntısı).
  if (isSsrDebug) {
    const debugInfo: IDebugInfo = {
      countryCode: (countryCode as string) || null,
      ipBasedCurrency: $ipBasedCurrency || null,
      market: market || null,
      parsedDisplaySettings: $parsedDisplaySettings || null,
      currencyFromDisplaySettings: $currencyFromDisplaySettings || null,
      defaultCurrency: $defaultCurrency || null,
      finalCurrency: {
        iso: $finalCurrencyIso,
        conversion: $finalCurrencyConversion,
      },
    }
    console.log('[SSR_DEBUG_INFO]', debugInfo)
  }

  // Worker experiment variant'ları SSR'da çözülür; raw experiments / A|B prop'ları asla pageProps'a konmaz.
  const workerExperiments = await fetchExperiments(EXPERIMENT_KEYS)
  const directFlightLabelQueryOverride = typeof query?.[DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY] === 'string' ? (query[DIRECT_FLIGHT_LABEL_QUERY_OVERRIDE_KEY] as string) : null
  const directFlightLabelVariant = resolveDirectFlightLabelVariant(workerExperiments, directFlightLabelQueryOverride)
  const searchFormVariant = workerExperiments[SEARCH_FORM_EXPERIMENT_KEY] || 'A'
  const uiVariants = { searchFormVariant, directFlightLabelVariant }
  attachHomepageUiVariantsToRequest(req, uiVariants)
  setHomepageUiVariantCookies(res, uiVariants)
  const endesignLocalesWithExperiments = applyDirectFlightLabelExperiment(endesignLocales, {
    variant: directFlightLabelVariant,
    market: market as string,
    language: language as string,
  })
  // Vizesiz uçuşları international ve christmas verilerinden filtrele
  let visaFreePromotedFlights: any[] = []
  if (market === 'tr' && homepage?.promotedFlights && Object.keys(visaFreeCountries).length > 0) {
    const international = homepage.promotedFlights?.international || []
    const christmas = homepage.promotedFlights?.christmas || []

    // International ve christmas verilerinden vizesiz ülkeler için olanları filtrele
    const visaFreeFromInternational = international.filter((flight: any) => {
      const countryCode = flight?.destinationCountryCode
      return countryCode && visaFreeCountries[countryCode]?.isVisaFree === true
    })

    const visaFreeFromChristmas = christmas.filter((flight: any) => {
      const countryCode = flight?.destinationCountryCode
      return countryCode && visaFreeCountries[countryCode]?.isVisaFree === true
    })

    // İki listeyi birleştir ve tekrarları kaldır (id'ye göre)
    const allVisaFree = [...visaFreeFromInternational, ...visaFreeFromChristmas]
    const uniqueVisaFree = allVisaFree.filter((flight, index, self) => index === self.findIndex((f) => f.id === flight.id))

    visaFreePromotedFlights = uniqueVisaFree
  }

  const promotedFlightsData = homepage?.promotedFlights
    ? {
        domestic: homepage.promotedFlights?.domestic || [],
        international: homepage.promotedFlights?.international || [],
        regional: homepage.promotedFlights?.regional || [],
        christmas: homepage.promotedFlights?.christmas || [],
        visaFree: visaFreePromotedFlights,
        ramadanHolidayDeals: homepage.promotedFlights?.['ramadan-holiday-deals'] || [],
      }
    : null

  const customOfferFlightsData = homepage?.customOfferFlights ?? null

  // Middleware artık url.locale set etmiyor; i18nLocale her zaman 'default' gelir.
  // Translation dosyaları 'en', 'it', 'tr' gibi language bazlı → header'dan gelen language'ı kullan.
  const isEnuygunHost = hostname.includes('enuygun.com')
  const translationLocale = language || (isEnuygunHost ? 'tr' : 'en')
  // Smart Banner — homepage'de tüm marketler (TR + Wingie). market/language yoksa BFF tr-tr default kullanır.
  // Wingie bannerları admin'de FLIGHT_TICKET domain'i ile tutuluyor; TR ise ENUYGUN_HOMEPAGE.
  let smartBanners: any[] | null = null

  if (isHomepage) {
    const resolvedMarket = (market as string) || 'tr'
    const resolvedLanguage = (language as string) || 'tr'
    const isWingieMarket = resolvedMarket !== 'tr'

    try {
      const fetchedBanners = await fetchSmartBanners(
        {
          inventory: 'PRODUCT_HOMEPAGE',
          domains: [isWingieMarket ? 'FLIGHT_TICKET' : 'ENUYGUN_HOMEPAGE'],
          pages: ['HOMEPAGE'],
          environments: [isMobileUA ? 'MOBILE_WEB' : 'DESKTOP_WEB'],
          // Locale prefix'li path (/en/, /ae-en/) homepage olarak /'a normalize et
          pathname: '/',
          isMobile: !!isMobileUA,
          market: resolvedMarket,
          language: resolvedLanguage,
        },
        hostname
      )
      smartBanners = fetchedBanners.length > 0 ? fetchedBanners : null
      console.log('[SmartBanner] Fetched banners count:', smartBanners?.length || 0, {
        market: resolvedMarket,
        language: resolvedLanguage,
        domains: isWingieMarket ? 'FLIGHT_TICKET' : 'ENUYGUN_HOMEPAGE',
      })
    } catch (error) {
      console.error('[SmartBanner] Error fetching banners:', error)
      smartBanners = null
    }
  }
  const isTabletDetected = req?.headers['cf-device-type'] === 'tablet'
  const isMobileDetected = req?.headers['x-ua-device'] === 'mobile' || isTabletDetected

  if (isSsrDebug) {
    console.log('[SSR] device', { isMobile: isMobileDetected, isTablet: isTabletDetected })
  }

  return {
    props: {
      ...(await serverSideTranslations(translationLocale as string, ['common'], nextI18nextConfig as any)),
      //@ts-ignore
      ua: {
        ...useUserAgent(req.headers['user-agent'] || ''),
        isMobile: isMobileDetected,
        isTablet: isTabletDetected,
      },
      page: finalResolvedPage,
      market,
      domain,
      fullLocale: `${market}-${language}` || 'tr-tr',
      language: language,
      currency: { iso: $finalCurrencyIso, conversion: $finalCurrencyConversion },
      currencyConversions: $currenyRates || null,
      res: $response || null,
      endpoint: API_ENDPOINT,
      footer: footer || null,
      endesignLocales: endesignLocalesWithExperiments || null,
      params: $params || null,
      path_locale: path_locale,
      locale: i18nLocale || language || null,
      host: !path_locale ? $host : `${$host}/${path_locale}`,
      resolvedUrl,
      promotedFlightsData,
      visaFreeCountries,
      residenceCountries,
      customOfferFlightsData,
      expansionLocale,
      smartBanners,
      deploymentDate: new Date().toISOString(),
      headerLinks: homepage?.headerLinks || null,
    },
  }
}

export default HomePage
